import type { FC, ReactNode } from "react";
import { useMemo } from "react";

import { Dialog } from "@ui/components/Dialog";
import { Select } from "@ui/components/Inputs";
import type { SelectOptionItem } from "@ui/components/Inputs/Select/Select";
import { Typography } from "@ui/components/Typography";
import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useTranslations } from "@ui/providers/useTranslations";
import { getBuilderImageResizeUrl } from "@ui/utils/getBuilderImageResizeUrl";
import { getOptimizedBackgroundImage } from "@ui/utils/getOptimizedBackgroundImage";
import classNames from "classnames";

import { Button } from "@jti/core-components/components/Button";
import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";
import { Loader } from "@jti/core-components/components/Loader";

import type { CountryItem } from "./hooks/types/CountrySelector.types";

import { useCountryDisplay, useGeoAndRedirect } from "./hooks";

import styles from "./CountrySelector.module.scss";

export type CountrySelectorProps = {
  list: CountryItem[];
  links?: { path: string; fileReference: string; displayLink: string }[];
  text?: string;
  countriesApi: string;
  accessibilityDialogLabel?: string;
  logoFileReference?: string;
  editor?: boolean;
  staticMode?: boolean;
  backgroundImage?: string;
  healthBanner?: ReactNode;
  richText?: ReactNode;
  selectCountryLabel?: string;
};

export type ParsedCountrySelectorItem = {
  label: string;
  value: string;
  fileReference?: string;
  url?: string;
  readonly?: boolean;
};

export const CountrySelector: FC<CountrySelectorProps> = ({
  list,
  links = [],
  text,
  countriesApi,
  accessibilityDialogLabel,
  editor = false,
  staticMode = false,
  backgroundImage,
  healthBanner,
  richText,
  logoFileReference,
  selectCountryLabel,
}) => {
  const { address, loading, enabledData, noGeo } = useGeoAndRedirect(countriesApi, editor, staticMode);
  const { filteredList, popupOpen, popupData } = useCountryDisplay(list, enabledData, staticMode);
  const { breakpoint } = useDeviceBreakpoint();
  const isMobile = breakpoint === "mobile";
  const { __ } = useTranslations();
  const parsedList: ParsedCountrySelectorItem[] = useMemo(() => {
    return filteredList.map((item) => ({
      label: item.countryName,
      value: item.countryCode,
      fileReference: item.fileReference,
    }));
  }, [filteredList]);
  const parsedLinks: ParsedCountrySelectorItem[] = useMemo(() => {
    return links.map((item) => ({
      label: item.displayLink,
      value: item.displayLink,
      url: item.path,
      fileReference: item.fileReference,
    }));
  }, [links]);
  const mergedList = [
    ...parsedList,
    ...parsedLinks,
    {
      label: __("countryselector.more_coming_soon"),
      value: __("countryselector.more_coming_soon"),
      readonly: true,
      fileReference: undefined,
    },
  ];

  const onChangeHandler = (option: SelectOptionItem | null) => {
    const country = filteredList.find((filteredListItem) => filteredListItem.countryCode === option?.value);
    const link = parsedLinks.find((parsedLinksItem) => parsedLinksItem.value === option?.value);
    const url = country?.url || link?.url;

    if (url) {
      window.location.href = url;
    }
  };

  const backgroundResizedImageUrl = getBuilderImageResizeUrl({ imageUrl: backgroundImage });
  const optimizedBackgroundImage = backgroundResizedImageUrl
    ? getOptimizedBackgroundImage(backgroundResizedImageUrl)
    : undefined;

  return (
    <div
      className={classNames(styles.container, {
        [styles.staticImage]: !backgroundImage,
      })}
      style={{
        "--background-image": optimizedBackgroundImage,
      }}
    >
      {loading ? (
        <Loader data-testid="loading-spinner" />
      ) : (
        <div className={styles.contentWrapper}>
          {!staticMode && (
            <div className={styles.tooltip}>
              <div className={styles.tooltipContent}>
                IP: {address.ipAddress}
                <br />
                Location: {address.countryRegion.isoCode}
              </div>
            </div>
          )}
          {logoFileReference && <ImageBehaviour className={styles.logo} src={logoFileReference} alt="logo" />}
          {text && <p className={styles.description}>{text}</p>}
          <div className={styles.selectWrapper}>
            <Select
              aria-label={__("countryselector.select.label") || __("countryselector.select.placeholder")}
              placeholder={__("countryselector.select.placeholder")}
              label={selectCountryLabel || __("countryselector.select.label")}
              options={mergedList.map((item) => {
                const imageUrl = !item?.readonly ? item?.fileReference : undefined;
                return imageUrl
                  ? { label: item.label, value: item.value, readonly: item?.readonly, imageUrl }
                  : { label: item.label, value: item.value, readonly: item?.readonly };
              })}
              onChange={onChangeHandler}
              isSearchable={false}
            />
          </div>
          {healthBanner && (
            <div className={styles.healthBanner}>
              <p className={styles.healthBannerText}>{healthBanner}</p>
            </div>
          )}
          {richText && <div className={styles.richTextWrapper}>{richText}</div>}
          {!staticMode && (
            <Dialog isOpened={Boolean(popupOpen && popupData)} closable ariaLabel={accessibilityDialogLabel}>
              <div className={styles.popupContentWrapper}>
                <Typography variant="h5">{`${__("countryselector.headline")} ${popupData?.ipLocationName}`}</Typography>
                <p>{`${__("countryselector.content.purchase")} ${popupData?.purchaseLocationName}?`}</p>
                <p>{`${__("countryselector.content.location")} ${address?.countryRegion.isoCode}?`}</p>
                <div className={styles.popupButtonsWrapper}>
                  <Button
                    size={isMobile ? "sm" : "md"}
                    href={noGeo ? undefined : popupData?.popupIpUrl}
                    richContent={
                      <>
                        <ImageBehaviour
                          alt={popupData?.ipLocationName ? `flag of the ${popupData.ipLocationName}` : "flag"}
                          src={popupData?.flagUrl}
                          className={styles.popupFlag}
                        />
                        {`${__("countryselector.content.goto")} ${popupData?.ipLocationName}`}
                      </>
                    }
                  />
                  <Button href={popupData?.popupPurchaseUrl} variant="text">
                    {__("countryselector.content.linkPurchase")}
                  </Button>
                </div>
              </div>
            </Dialog>
          )}
        </div>
      )}
    </div>
  );
};
